import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { useFetch } from '@reveni-io/reveni-ui'

import { deliveryMethodsTabs } from '@/configuration/pages/ConfigurationPage/LogisticsConfiguration/DeliveryMethods/DeliveryMethodsConfiguration/constants'
import configurationServices from '@/configuration/services/services'
import routes from '@/Router/routes.constants'

import { initCriterion } from './constants'
import {
  transformRequestCreateAvailabilityCriterion,
  transformRequestEditAvailabilityCriterion,
  transformResponseGetAvailabilityCriterion,
} from './transformers'

const useAvailabilityCriterion = ({ id, storeId, setToastInfo, storeCurrencyCode }) => {
  const isDuplicate = id?.includes('duplicate-')
  const isNew = id === 'new'
  const criterionId = isDuplicate ? id.replace('duplicate-', '') : id
  const availabilityCriteriaTabRoute = `${routes.configurationPage.logistics.deliveryMethods}?tab=${deliveryMethodsTabs.availabilityCriteria}`

  const { t } = useTranslation()
  const navigate = useNavigate()

  const [criterion, setCriterion] = useState(initCriterion)
  const [hasChanges, setHasChanges] = useState(false)
  const [errors, setErrors] = useState([])
  const originalCriterion = useRef(initCriterion)

  const { fetchData: fetchAvailabilityCriterion, loading: fetchingAvailabilityCriterion } = useFetch(
    configurationServices.getAvailabilityCriterion,
    { doInitialRequest: false, transformResponse: transformResponseGetAvailabilityCriterion }
  )

  const { fetchData: createAvailabilityCriterion, loading: creatingAvailabilityCriterion } = useFetch(
    configurationServices.createAvailabilityCriterion,
    { doInitialRequest: false, transformRequest: transformRequestCreateAvailabilityCriterion }
  )

  const { fetchData: editAvailabilityCriterion, loading: editingAvailabilityCriterion } = useFetch(
    configurationServices.editAvailabilityCriterion,
    { doInitialRequest: false, transformRequest: transformRequestEditAvailabilityCriterion }
  )

  const loadCriterion = useCallback(async () => {
    if (isNew) return

    try {
      const data = await fetchAvailabilityCriterion({ storeId, criterionId })
      originalCriterion.current = data
      setCriterion(data)
      if (isDuplicate) setHasChanges(true)
    } catch (error) {
      setToastInfo({ show: true, message: error.errorMessage, type: 'error' })
    }
  }, [isNew, isDuplicate, fetchAvailabilityCriterion, storeId, criterionId, setToastInfo])

  useEffect(() => {
    loadCriterion()
  }, [loadCriterion])

  const onChangeCriterion = paramsEdited => {
    setCriterion(prev => ({ ...prev, ...paramsEdited }))
    setHasChanges(true)
  }

  const revertChanges = () => {
    if (isNew || isDuplicate) {
      navigate(availabilityCriteriaTabRoute)
      return
    }

    setCriterion(originalCriterion.current)
    setErrors([])
    setHasChanges(false)
  }

  const saveChanges = async () => {
    try {
      if (isNew || isDuplicate) {
        await createAvailabilityCriterion({ storeId, criterion, defaultCurrency: storeCurrencyCode })
      } else {
        await editAvailabilityCriterion({ storeId, criterionId, criterion, defaultCurrency: storeCurrencyCode })
      }
      setToastInfo({ show: true, message: t('Cambios guardados'), type: 'success' })
      navigate(availabilityCriteriaTabRoute)
    } catch (error) {
      setToastInfo({ show: true, message: error.errorMessage, type: 'error' })
    }
  }

  return {
    criterion,
    fetchingAvailabilityCriterion,
    creatingAvailabilityCriterion,
    editingAvailabilityCriterion,
    hasChanges,
    errors,
    setErrors,
    onChangeCriterion,
    revertChanges,
    saveChanges,
    isNew,
    isDuplicate,
  }
}

export default useAvailabilityCriterion
